<template>
  <ValidationObserver ref="observer-form" tag="div" class="uk-form-stacked">
    <div class="uk-margin">
      <ValidationProvider v-slot="{ errors }" rules="required">
        <label class="uk-form-label uk-text-bold" for="name" :class="{ 'uk-text-danger': errors.length }">Practice Name</label>
        <div class="uk-form-controls">
          <input id="name" v-model="form.name" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="name" :disabled="disabled" data-cy="practice-form-name">
        </div>
        <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
      </ValidationProvider>
    </div>

    <div class="uk-margin">
      <ValidationProvider v-slot="{ errors }" rules="required|email">
        <label class="uk-form-label uk-text-bold" for="email" :class="{ 'uk-text-danger': errors.length }">Notification Email</label>
        <div class="uk-form-controls">
          <input id="email" v-model="form.email" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="email" :disabled="disabled" data-cy="practice-form-email">
        </div>
        <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
      </ValidationProvider>
    </div>

    <div class="uk-margin">
      <ValidationProvider v-slot="{ errors }" rules="required">
        <label class="uk-form-label uk-text-bold" for="area" :class="{ 'uk-text-danger': errors.length }">Area</label>
        <div class="uk-form-controls">
          <multiselect id="area" v-model="form.area" :class="{ 'multiselect-error': errors.length }" placeholder="Pick one. Type to filter." label="name" track-by="uid" :options="$helpers.areas" :searchable="true" :hide-selected="true" :disabled="disabled" data-cy="practice-form-area" />
        </div>
        <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
      </ValidationProvider>
    </div>

    <div class="uk-form-label uk-text-bold" :class="{ 'uk-text-danger': locationError > 0 }">Location</div>
    <div class="uk-margin-small">
      <ValidationProvider v-slot="{ errors }" name="lh" rules="required">
        <div class="uk-form-controls">
          <div class="uk-form-label" for="house" :class="{ 'uk-text-danger': errors.length }">House Number/Name</div>
          <input id="house" v-model="form.location.house" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="house" :disabled="disabled" data-cy="practice-form-house">
        </div>
        <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
      </ValidationProvider>
    </div>

    <div class="uk-margin-small">
      <ValidationProvider v-slot="{ errors }" name="ls" rules="required">
        <div class="uk-form-controls">
          <div class="uk-form-label" for="street" :class="{ 'uk-text-danger': errors.length }">Street Name</div>
          <input id="street" v-model="form.location.street" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="street" :disabled="disabled" data-cy="practice-form-street">
        </div>
        <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
      </ValidationProvider>
    </div>

    <div class="uk-margin-small">
      <ValidationProvider v-slot="{ errors }" name="lc" rules="required">
        <div class="uk-form-controls">
          <div class="uk-form-label" for="city" :class="{ 'uk-text-danger': errors.length }">City/Town</div>
          <input id="city" v-model="form.location.city" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="city" :disabled="disabled" data-cy="practice-form-city">
        </div>
        <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
      </ValidationProvider>
    </div>

    <div class="uk-margin-small">
      <ValidationProvider v-slot="{ errors }" name="lp" rules="required">
        <div class="uk-form-controls">
          <div class="uk-form-label" for="postcode" :class="{ 'uk-text-danger': errors.length }">Postcode</div>
          <input id="postcode" v-model="form.location.postcode" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="postcode" :disabled="disabled" data-cy="practice-form-postcode">
        </div>
        <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
      </ValidationProvider>
    </div>

    <div class="uk-margin-small">
      <ValidationProvider v-slot="{ errors }" name="lglat" :rules="{ required: true, regex: /^(\+|-)?(?:90(?:(?:\.0{1,6})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,6})?))$/ }">
        <div class="uk-form-controls">
          <div class="uk-form-label" for="lat" :class="{ 'uk-text-danger': errors.length }">Latitude</div>
          <input id="lat" v-model="form.location.geopoint.lat" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="lat" :disabled="disabled" data-cy="practice-form-latitude">
        </div>
        <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
      </ValidationProvider>
    </div>

    <div class="uk-margin-small">
      <ValidationProvider v-slot="{ errors }" name="lglong" :rules="{ required: true, regex: /^(\+|-)?(?:180(?:(?:\.0{1,6})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,6})?))$/ }">
        <div class="uk-form-controls">
          <div class="uk-form-label" for="long" :class="{ 'uk-text-danger': errors.length }">Longitude</div>
          <input id="long" v-model="form.location.geopoint.long" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="long" :disabled="disabled" data-cy="practice-form-longitude">
        </div>
        <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
      </ValidationProvider>
    </div>

    <div class="uk-margin">
      <ValidationProvider v-slot="{ errors }" rules="required">
        <label class="uk-form-label uk-text-bold" for="phone-primary" :class="{ 'uk-text-danger': errors.length }">Phone Number - Practice</label>
        <div class="uk-form-controls">
          <input id="phone-primary" v-model="local.phone.primary" pattern="^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="phone-primary" :disabled="disabled" data-cy="practice-form-phone-primary">
        </div>
        <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
        <div v-if="checkPhoneNumbers.primary.type && checkPhoneNumbers.primary.type !== 'FIXED_LINE'" class="form-warning">This number seems to be of <b>incorrect type</b>. The required type is Landline.</div>
        <div v-if="!checkPhoneNumbers.primary.valid" class="form-warning">This number seems to be <b>invalid</b>.</div>
        <div v-if="!checkPhoneNumbers.primary.possible" class="form-warning">This number seems to be <b>impossible</b>.</div>
      </ValidationProvider>
    </div>

    <div class="uk-margin">
      <ValidationProvider v-slot="{ errors }" rules="required">
        <label class="uk-form-label uk-text-bold" for="phone-secondary" :class="{ 'uk-text-danger': errors.length }">Phone Number - On-Call & Notifications</label>
        <div class="uk-text-meta">This number should be a mobile number capable of receiving Text Messages (SMS).</div>
        <div class="uk-form-controls">
          <input id="phone-secondary" v-model="local.phone.secondary" pattern="^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="phone-secondary" :disabled="disabled" data-cy="practice-form-phone-secondary">
        </div>
        <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
        <div v-if="checkPhoneNumbers.secondary.type && checkPhoneNumbers.secondary.type !== 'MOBILE'" class="form-warning">This number seems to be of <b>incorrect type</b>. The required type is Mobile.</div>
        <div v-if="!checkPhoneNumbers.secondary.valid" class="form-warning">This number seems to be <b>invalid</b>.</div>
        <div v-if="!checkPhoneNumbers.secondary.possible" class="form-warning">This number seems to be <b>impossible</b>.</div>
      </ValidationProvider>
    </div>

    <div class="uk-margin">
      <div class="uk-form-label uk-text-bold">Features</div>
      <template v-for="feature in $helpers.practiceFeatures">
        <div :key="feature.id" class="uk-margin-small">
          <div>{{ feature.value }}</div>
          <switch-button :labels="{ false: 'No', true: 'Yes' }" :status.sync="form.features[feature.id].status" style="margin: 5px 0;" :data-cy="`practice-form-feature-btn-${feature.id}`" />
          <textarea v-model="form.features[feature.id].notes" class="uk-textarea" cols="30" rows="5" :data-cy="`practice-form-feature-text-${feature.id}`" />
        </div>
      </template>
    </div>

    <div class="uk-margin">
      <div class="uk-form-label uk-text-bold">Notifications</div>
      <div class="uk-form-controls uk-margin-small-top">
        <label class="uk-margin-small-right uk-margin-small-left" for="email-checkbox"><input id="email-checkbox" v-model="form.notifications.email" class="uk-checkbox" type="checkbox" data-cy="practice-form-notification-check-email"> Email</label>
        <label class="uk-margin-small-right uk-margin-small-left" for="sms-checkbox"><input id="sms-checkbox" v-model="form.notifications.phone" class="uk-checkbox" type="checkbox" data-cy="practice-form-notification-check-sms"> SMS</label>
      </div>
    </div>

    <div class="uk-margin">
      <div class="uk-form-label uk-text-bold">Account Status</div>
      <div class="uk-form-controls uk-margin-small">
        <select v-model="form.status" class="uk-select" data-cy="practice-form-status">
          <template v-for="status in this.$helpers.status.dentist">
            <option :key="status.id" :value="status.id">{{ status.value }}</option>
          </template>
        </select>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import SwitchButton from '@/components/SwitchButton.vue'

export default {
  name: 'ComponentFormPractice',

  components: {
    ValidationProvider,
    ValidationObserver,
    SwitchButton
  },

  props: {
    type: {
      type: String,
      required: true
    },

    dentist: {
      type: Object,
      required: false,
      default: null
    }
  },

  data() {
    return {
      observer: null,
      local: {
        phone: {
          primary: null,
          secondary: null
        }
      },
      form: {
        email: null,
        area: null,
        phone: {
          primary: null,
          secondary: null
        },
        name: null,
        location: {
          house: null,
          street: null,
          city: null,
          postcode: null,
          geopoint: {
            lat: null,
            long: null
          }
        },
        features: {},
        notifications: {
          email: true,
          phone: true
        },
        status: 0
      }
    }
  },

  computed: {
    disabled() {
      // return this.dentist && this.dentist.r ? true : false
      return false
    },

    checkPhoneNumbers() {
      const primary = this.local.phone.primary ? this.$parsePhone(this.local.phone.primary, 'GB') : null
      const secondary = this.local.phone.secondary ? this.$parsePhone(this.local.phone.secondary, 'GB') : null
      const numbers = {
        primary: {
          possible: true,
          valid: true,
          type: 'FIXED_LINE'
        },
        secondary: {
          possible: true,
          valid: true,
          type: 'MOBILE'
        }
      }

      if (primary) {
        numbers.primary = {
          number: primary.number,
          possible: primary.isPossible(),
          valid: primary.isValid(),
          type: primary.getType()
        }
      }

      if (secondary) {
        numbers.secondary = {
          number: secondary.number,
          possible: secondary.isPossible(),
          valid: secondary.isValid(),
          type: secondary.getType()
        }
      }

      return numbers
    },

    locationError() {
      let response = 0
      const props = ['lh', 'ls', 'lc', 'lp', 'lglat', 'lglong']

      if (this.observer && this.observer.errors && props.every((prop) => this.observer.errors.hasOwnProperty(prop))) {
        const { errors } = this.observer
        response = errors.lh.length + errors.ls.length + errors.lc.length + errors.lp.length + errors.lglat.length + errors.lglong.length
      }

      return response
    }
  },

  watch: {
    'local.phone.primary': {
      handler() {
        this.form.phone.primary = this.checkPhoneNumbers.primary.number
      }
    },

    'local.phone.secondary': {
      handler() {
        this.form.phone.secondary = this.checkPhoneNumbers.secondary.number
      }
    },

    form: {
      deep: true,
      handler() {
        this.$emit('update:form', this.form)
      }
    }
  },

  created() {
    const features = {}

    this.$helpers.practiceFeatures.forEach((feature) => {
      features[feature.id] = {
        status: false,
        notes: null
      }
    })

    if (this.type === 'manage') {
      this.form.email = this.dentist.email
      this.form.area = this.$helpers.areas.find((area) => area.uid === this.dentist.area)
      this.form.name = this.dentist.name
      this.local.phone.primary = this.$parsePhone(this.dentist.phone.primary, 'GB').format('NATIONAL').replace(/ /g, '')
      this.local.phone.secondary = this.$parsePhone(this.dentist.phone.secondary, 'GB').format('NATIONAL').replace(/ /g, '')
      this.form.phone.primary = this.dentist.phone.primary
      this.form.phone.secondary = this.dentist.phone.secondary
      this.form.location.house = this.dentist.location.house
      this.form.location.street = this.dentist.location.street
      this.form.location.city = this.dentist.location.city
      this.form.location.postcode = this.dentist.location.postcode
      this.form.location.geopoint.lat = this.dentist.location.geopoint.latitude
      this.form.location.geopoint.long = this.dentist.location.geopoint.longitude
      this.form.features = { ...features, ...this.dentist.features }
      this.form.notifications = this.dentist.notifications
      this.form.status = this.dentist.status
    } else {
      this.form.features = features
    }
  },

  mounted() {
    this.observer = this.$refs['observer-form']
  },

  methods: {
    async validate() {
      return this.observer.validate()
    }
  }
}
</script>
